import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { IAbsencesState } from "./types";
import { IIDAStore } from "../types";
import { endOfDay, startOfDay } from "date-fns";
import { Absence } from "../../models/absences";
import { AbsenceType } from "../../models/absenceTypes-model";
import { absencesService } from "../../services/Absences.service";
import { eSalerySystem } from "../../models/institution-model";
import store from "../../store";
import { StoreCache, storeCachePeriode } from "../storeCache";
import { IPeriode } from "../../utils/dateUtils";

export const getInitialState = (): IAbsencesState => {
  return {
    searching: false,
    absences: {},
    absencesKeys: {},
    absenceTypes: [],
    storeCache: new StoreCache()
  };
};

const namespaced = true;

export const getters: GetterTree<IAbsencesState, IIDAStore> = {
  GetAbsences: (state) => state.absences,
  GetAbsencesByEmployeeIds: (state, getters) => (employeeIds: number[]) => {
    const list: Absence[] = [];
    for (let index = 0; index < employeeIds.length; index++) {
      list.push(getters.GetAbsencesByEmployeeId(employeeIds[index]));
    }
    return list;
  },
  /* AbsenceType */
  GetAbsenceTypeById: (state) => (absenceTypeId: number) => {
    return state.absenceTypes.find((a) => a.id === absenceTypeId);
  },
  GetAbsenceTypes: (state) => state.absenceTypes,
  GetAbsencesByEmployeeId: (state) => (employeeId: number) => {
    if (state.absencesKeys[employeeId]) {
      return state.absencesKeys[employeeId];
    } else {
      return [];
    }
  },
  GetAbsencesByEmployeeIdAndDate: (state, getters) => (employeeId: number, dato: Date) => {
    const absences: Absence[] = getters.GetAbsencesByEmployeeId(employeeId);
    const list: Absence[] = [];
    for (let index = 0; index < absences.length; index++) {
      const absence = absences[index];
      if (absence.isAbsenceActiveOnDate(dato)) {
        list.push(absence);
      }
    }
    return list;
  },

  GetAbsencesByDate: (state, getters) => (date: Date) => {
    return Object.values(state.absences).filter((ab) => ab.isAbsenceActiveOnDate(date));
  }
};

export const actions: ActionTree<IAbsencesState, IIDAStore> = {
  reset({ commit }) {
    commit("reset");
  },
  fetchAbsences(
    { commit, state },
    query: { from: Date; to: Date; forceUpdate: boolean }
  ): Promise<Absence[]> {
    return new Promise((resolve, reject) => {
      const from: Date = startOfDay(query.from);
      const to: Date = endOfDay(query.to);
      const periode = { fra: from, tilOgMed: to } as IPeriode;
      if (query.forceUpdate || !state.storeCache.isCached(periode)) {
        try {
          absencesService.fetchAbsences(from, to).then((absences) => {
            commit("updateAbsences", { absences, from, to });
            return resolve(absences);
          });
        } catch (error) {
          return reject(error);
        }
      } else {
        const absences: Absence[] = Object.values(state.absences).filter(
          (ab) => (ab.end_date >= from || ab.is_open) && ab.start_date < to
        );
        return resolve(absences);
      }
    });
  },
  fetchAbsenceById({ commit, state }, id: number): Promise<Absence | undefined> {
    return new Promise((resolve, reject) => {
      const absence: Absence | undefined = Object.values(state.absences).find((a) => a.id === id);
      if (!absence) {
        absencesService.fetchAbsenceById(id).then((absence) => {
          if (absence) {
            commit("updateAbsences", {
              absences: [absence],
              from: absence.start_date,
              to: absence.end_date
            });
          }
          resolve(absence);
        });
      } else {
        resolve(absence);
      }
    });
  },

  fetchAbsenceTypes() {
    return new Promise((resolve, reject) => {
      store
        .dispatch("absences/fetchAllAbsenceTypes")
        .then((allAbsenceTypes: AbsenceType[]) => {
          resolve(allAbsenceTypes);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  fetchAllAbsenceTypes({ commit, state }) {
    return new Promise((resolve, reject) => {
      if (state.absenceTypes.length === 0) {
        try {
          absencesService.fetchAbsenceTypes().then((absenceTypes) => {
            const salerySystem: number = store.getters["institutions/getSalerySystem"]();
            let nonePlannedCodes: string[] = [];
            switch (salerySystem) {
              case eSalerySystem.Manuel:
                nonePlannedCodes = AbsenceType.salerySystemManuelnoneplanned;
                break;
              case eSalerySystem.SD:
                nonePlannedCodes = AbsenceType.salerySystemSDnoneplanned;
                break;
              case eSalerySystem.KMD:
                nonePlannedCodes = AbsenceType.salerySystemKMDnoneplanned;
                break;
              default:
                break;
            }
            absenceTypes.forEach(
              (at) => (at.nonePlanned = nonePlannedCodes.some((npc) => npc === at.code))
            );
            commit("updateAbsenceTypes", absenceTypes);
            resolve(absenceTypes);
          });
        } catch (error) {
          return reject(error);
        }
      } else {
        resolve(state.absenceTypes);
      }
    });
  },
  createAbsence({ commit }, absence: Absence) {
    return new Promise((resolve, reject) => {
      absencesService
        .createAbsence(absence)
        .then((absence: Absence) => {
          commit("updateAbsences", {
            absences: [absence],
            from: absence.start_date,
            to: absence.Periode.tilOgMed
          });
          resolve(absence);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateAbsence({ commit }, absence: Absence) {
    return new Promise((resolve, reject) => {
      absencesService
        .updateAbsence(absence)
        .then((absence: Absence) => {
          commit("updateAbsences", { absences: [absence], from: new Date(), to: new Date() });
          resolve(absence);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteAbsence({ commit }, absenceId: number) {
    return new Promise((resolve, reject) => {
      absencesService
        .deleteAbsence(absenceId)
        .then((absence: any) => {
          commit("removeAbsence", absenceId);
          resolve(absence);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  createAbsenceType({ commit }, absenceType: AbsenceType) {
    return new Promise((resolve, reject) => {
      absencesService
        .createAbsenceType(absenceType)
        .then((absenceType: AbsenceType) => {
          commit("insertAbsenceType", absenceType);
          resolve(absenceType);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateAbsenceType({ commit }, absenceType: AbsenceType) {
    return new Promise((resolve, reject) => {
      absencesService
        .updateAbsenceType(absenceType)
        .then((absenceType: AbsenceType) => {
          commit("updateAbsenceType", absenceType);
          resolve(absenceType);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteAbsenceType({ commit }, absenceTypeId: number) {
    return new Promise((resolve, reject) => {
      absencesService
        .deleteAbsenceType(absenceTypeId)
        .then((absenceType: any) => {
          commit("removeAbsenceType", absenceTypeId);
          resolve(absenceType);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
};

export const mutations: MutationTree<IAbsencesState> = {
  reset(state: IAbsencesState) {
    Object.assign(state, getInitialState());
  },
  removeAbsence(state: IAbsencesState, absenceId: number) {
    const oldAbsence = state.absences[absenceId];

    if (oldAbsence) {
      state.absencesKeys[oldAbsence.employee_id] = state.absencesKeys[
        oldAbsence.employee_id
      ].filter((a) => a.id !== oldAbsence.id);
    }
    delete state.absences[absenceId];
  },
  insertAbsenceType(state: IAbsencesState, absenceType: AbsenceType) {
    state.absenceTypes.push(absenceType);
  },
  removeAbsenceType(state: IAbsencesState, absenceTypeId: number) {
    const index = state.absenceTypes.findIndex((a) => a.id === absenceTypeId);
    state.absenceTypes.splice(index, 1);
  },
  updateAbsenceType(state: IAbsencesState, absenceType: AbsenceType) {
    const index = state.absenceTypes.findIndex((a) => a.id === absenceType.id);
    state.absenceTypes[index] = absenceType;
  },
  updateAbsences(state: IAbsencesState, payload: { absences: Absence[]; from: Date; to: Date }) {
    for (let index = 0; index < payload.absences.length; index++) {
      const absence: Absence = payload.absences[index];
      state.absences[absence.id] = absence;
      const key = absence.employee_id;
      let employment = state.absencesKeys[key];
      if (!employment) {
        employment = state.absencesKeys[key] = [];
      }
      const ix = employment.findIndex((d) => d.equals(absence));
      if (ix > -1) {
        employment[ix] = absence;
      } else {
        employment.push(absence);
      }
    }

    if (payload.from && payload.to) {
      state.storeCache.addCache(new storeCachePeriode({ fra: payload.from, tilOgMed: payload.to }));
    }
  },
  updateAbsenceTypes(state: IAbsencesState, absenceTypes: AbsenceType[]) {
    state.absenceTypes = [
      ...state.absenceTypes.filter(
        (oldAbsenceTypes) => !absenceTypes.find((newAT) => newAT.equals(oldAbsenceTypes))
      ),
      ...absenceTypes
    ];
  }
};

export const absencesStoreModule: Module<IAbsencesState, IIDAStore> = {
  namespaced: namespaced,
  state: getInitialState(),
  getters: getters,
  actions: actions,
  mutations: mutations
};
